import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, Mic, MicOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MessageBubble from "@/components/MessageBubble";
import MachineCard from "@/components/MachineCard";
import TypingIndicator from "@/components/TypingIndicator";
import LeadForm from "@/components/LeadForm";
import { toast } from "sonner";

// Use REACT_APP_BACKEND_URL if available (for preview/production), otherwise use relative URL (for proxy)
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

const MAQUITO_URL = "https://customer-assets.emergentagent.com/job_maquito-helper/artifacts/ozr0qru4_maquito-sentado.png";

export default function Chat() {
  const { conversationId: urlConversationId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [conversationId, setConversationId] = useState(urlConversationId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLeadSubmit = async (formData) => {
    setLeadCaptured(true);
    
    // Send lead data to backend (include empresa if provided)
    let leadMessage = `${formData.nombre}, ${formData.email}, ${formData.celular}`;
    if (formData.empresa && formData.empresa.trim()) {
      leadMessage += `, ${formData.empresa}`;
    }
    
    let displayContent = `Nombre: ${formData.nombre}\nEmail: ${formData.email}\nCelular: ${formData.celular}`;
    if (formData.empresa && formData.empresa.trim()) {
      displayContent += `\nEmpresa: ${formData.empresa}`;
    }
    
    setMessages([{
      role: "user",
      content: displayContent,
      timestamp: new Date().toISOString()
    }]);
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/message`, {
        message: leadMessage,
        conversation_id: conversationId
      });

      const { conversation_id, message: botMessage, machines } = response.data;
      
      if (!conversationId) {
        setConversationId(conversation_id);
        navigate(`/chat/${conversation_id}`, { replace: true });
      }

      setMessages(prev => [...prev, {
        role: "assistant",
        content: botMessage,
        machines: machines,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al enviar los datos");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBypass = async () => {
    setLeadCaptured(true);
    
    setMessages([{
      role: "user",
      content: "qa",
      timestamp: new Date().toISOString()
    }]);
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/message`, {
        message: "qa",
        conversation_id: conversationId
      });

      const { conversation_id, message: botMessage } = response.data;
      
      if (!conversationId) {
        setConversationId(conversation_id);
        navigate(`/chat/${conversation_id}`, { replace: true });
      }

      setMessages(prev => [...prev, {
        role: "assistant",
        content: botMessage,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        message: text,
        conversation_id: conversationId
      });

      const { conversation_id, message: botMessage, machines } = response.data;
      
      if (!conversationId) {
        setConversationId(conversation_id);
        navigate(`/chat/${conversation_id}`, { replace: true });
      }

      const assistantMessage = {
        role: "assistant",
        content: botMessage,
        machines: machines,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error al enviar mensaje. Por favor intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendClick = () => {
    sendMessage(inputText);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputText);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const audioChunks = [];

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        await sendVoiceMessage(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      toast.info("🎤 Grabando... Haz clic de nuevo para enviar");
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("No se pudo acceder al micrófono. Verifica los permisos.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setIsRecording(false);
      setIsLoading(true);
    }
  };

  const sendVoiceMessage = async (audioBlob) => {
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const response = await axios.post(`${API}/chat/voice`, {
          audio_base64: base64Audio,
          conversation_id: conversationId
        });

        const { conversation_id, message: botMessage, machines } = response.data;
        
        if (!conversationId) {
          setConversationId(conversation_id);
          navigate(`/chat/${conversation_id}`, { replace: true });
        }

        // We don't know the exact transcription, so we'll just show the bot response
        const assistantMessage = {
          role: "assistant",
          content: botMessage,
          machines: machines,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, assistantMessage]);
        setIsLoading(false);
        toast.success("✓ Audio procesado correctamente");
      };
    } catch (error) {
      console.error("Error sending voice message:", error);
      toast.error("Error al procesar el audio. Por favor intenta de nuevo.");
      setIsLoading(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50" data-testid="chat-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-4 shadow-sm">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/')}
          className="rounded-full"
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <img src={MAQUITO_URL} alt="Maquito" className="h-10 w-10 rounded-full" style={{ height: 'auto' }} data-testid="maquito-avatar" />
        <div>
          <h1 className="font-bold text-slate-900">Maquito</h1>
          <p className="text-xs text-green-600">En línea</p>
        </div>
      </header>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4" data-testid="messages-container">
        {/* Show LeadForm if lead not captured yet */}
        {!leadCaptured && messages.length === 0 && (
          <LeadForm onSubmit={handleLeadSubmit} onBypass={handleBypass} />
        )}
        
        {/* Show messages after lead is captured */}
        {messages.map((message, index) => (
          <div key={index}>
            <MessageBubble 
              role={message.role} 
              content={message.content} 
              timestamp={message.timestamp}
            />
            {message.machines && message.machines.length > 0 && (
              <div className="mt-4 space-y-3 ml-12" data-testid="machines-list">
                {message.machines.map((machine, machineIndex) => (
                  <MachineCard key={machineIndex} machine={machine} />
                ))}
              </div>
            )}
          </div>
        ))}
        {isLoading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {/* Input Area - only show after lead is captured */}
      {leadCaptured && (
        <div className="bg-white border-t border-slate-200 px-4 py-4 shadow-lg" data-testid="input-container">
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center gap-2">
              <Button
                onClick={toggleRecording}
                className={`rounded-full p-4 transition-all ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 pulse-green' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
                disabled={isLoading}
                data-testid="voice-button"
              >
                {isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu consulta sobre maquinaria..."
                className="flex-1 bg-white border-2 border-slate-100 focus:border-green-500 rounded-full py-6 px-6 text-base"
                disabled={isLoading || isRecording}
                data-testid="message-input"
              />
              <Button
                onClick={handleSendClick}
                disabled={!inputText.trim() || isLoading || isRecording}
                className="bg-green-600 hover:bg-green-700 text-white rounded-full p-4"
                data-testid="send-button"
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}